import { Alert, Button, ButtonProps, Result, ResultProps } from "antd"
import React, { FC, useState } from "react"
import { useHistory } from "react-router-dom"
import { BookingRestrictionErrors, useLogoutMutation } from "../../generated/graphql"
import { URL_BASE, URL_LOGIN } from "../../models/url"
import { ReloadOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { safeStorage } from "../../helpers/safeStorage"
import { takeAppliedDiscount } from "../../containers/CreateBooking/BookingSummary/BookingPricePreview/discountCodeMessages"
import { renderAmount } from "../../helpers/renderAmount"
import type { CurrencyShort } from "../../helpers/renderAmount"

export type ResultKeys =
    | "unknown"
    | "success"
    | "test"
    | "succesfullyBooked"
    | "successfullyOnWaitingList"
    | "interestRegistered"
    | "paymentInitFailed"
    | "loggedOut"
    | "alreadyEnrolled"
    | "courseNotFound"
    | "notDeletableHasBookings"
    | "errorFetchingData"
    | "bookingRestrictionError"
    | "NoBookingRestrictionFound"
    | "BookingRestrictionNotWithinTimeFrame"
    | "BookingRestrictionExpiredAndShowToAllIsSet"
    | "AttendeeHasNoBookingInPrerequisiteCourse"
    | "AttendeeHasWaitlistBookingInPrerequisiteCourse"
    | "AttendeeHasBookingInPrerequisiteCourse"
    | "AttendeeIsNotAMember"
    | "AllowedToBook"
    | "lateEntryNoEligibleLessons"
    | "lateEntryMinimumNotReached"
    | "lateEntryPricingUnavailable"

const knownResultKeys: Array<ResultKeys> = [
    "unknown",
    "success",
    "test",
    "succesfullyBooked",
    "successfullyOnWaitingList",
    "interestRegistered",
    "paymentInitFailed",
    "loggedOut",
    "alreadyEnrolled",
    "courseNotFound",
    "notDeletableHasBookings",
    "errorFetchingData",
    "bookingRestrictionError",
    "NoBookingRestrictionFound",
    "BookingRestrictionNotWithinTimeFrame",
    "BookingRestrictionExpiredAndShowToAllIsSet",
    "AttendeeHasNoBookingInPrerequisiteCourse",
    "AttendeeHasWaitlistBookingInPrerequisiteCourse",
    "AttendeeHasBookingInPrerequisiteCourse",
    "AttendeeIsNotAMember",
    "AllowedToBook",
    "lateEntryNoEligibleLessons",
    "lateEntryMinimumNotReached",
    "lateEntryPricingUnavailable",
]

type ResultScreenProps = {
    resultType?: ResultKeys | string
    errorMsg?: string
    customButton?: React.ReactNode
}

const standardErrorTitle = "Unbekannter Fehler"
const standardErrorMessage = "Es tut uns leid, da ist etwas schief gelaufen. Bitte versuchen Sie es später noch einmal."

const ResultScreen: FC<ResultScreenProps> = ({ resultType, errorMsg = standardErrorMessage, customButton }) => {
    const history = useHistory()
    // Gesetzt von BookingSummary, wenn die Warteliste wegen fehlender Voraussetzung gewählt wurde.
    const waitlistReason = new URLSearchParams(history.location.search).get("reason")
    const waitlistBecausePrerequisiteMissing = waitlistReason === "prerequisite"
    const { t } = useTranslation()
    const [logoutLoading, setLogoutLoading] = useState(false)
    const [logoutUser] = useLogoutMutation()
    // Eingelöster Rabattcode der gerade abgeschlossenen Buchung (einmalig aus dem Speicher, Skizze 10A).
    const [appliedDiscount] = useState(() => (resultType === "succesfullyBooked" || resultType === "successfullyOnWaitingList" ? takeAppliedDiscount() : null))
    const appliedDiscountLine = appliedDiscount ? (
        <Alert
            key='applied-discount'
            type='success'
            showIcon
            style={{ textAlign: "left", marginBottom: 16 }}
            data-testid='result-applied-discount'
            message={t(resultType === "successfullyOnWaitingList" ? "discountCode.resultLineWaitlist" : "discountCode.resultLine", {
                // Auf der Warteliste ist der Code nur vorgemerkt, eingelöst wird er erst mit dem Platz.
                defaultValue: resultType === "successfullyOnWaitingList" ? "Rabattcode {{code}} vorgemerkt: Bekommst du einen Platz, sparst du {{amount}}." : "Rabattcode {{code}} eingelöst, du sparst {{amount}}.",
                code: appliedDiscount.code,
                amount: renderAmount({ amount: appliedDiscount.amount, currency: appliedDiscount.currency as CurrencyShort | undefined }),
            })}
        />
    ) : null

    const standardErrorTitle = t("resultScreen.standardErrorTitle", "Unbekannter Fehler")
    const standardErrorMessage = t("resultScreen.standardErrorMessage", "Es tut uns leid, da ist etwas schief gelaufen. Bitte versuchen Sie es später noch einmal.")

    if (resultType === "unknown" && errorMsg !== standardErrorMessage) {
        console.log("==> errorMsg: ", errorMsg)
    }

    const isOfTypeResultKeys = (keyInput: string): keyInput is ResultKeys => {
        return knownResultKeys.includes(keyInput as ResultKeys)
    }

    const StandardButton = ({ type }: { type?: ButtonProps["type"] }) => {
        return (
            <Button type={type ?? "primary"} onClick={() => history.push(URL_BASE)}>
                {t("resultScreen.backToSearch", "Zurück zur Kurssuche")}
            </Button>
        )
    }

    const BackButton = ({ type }: { type?: ButtonProps["type"] }) => {
        return (
            <Button type={type ?? "primary"} onClick={() => history.goBack()}>
                {t("resultScreen.back", "Zurück")}
            </Button>
        )
    }

    const logout = async ({ goToLink }: { goToLink: string }) => {
        setLogoutLoading(true)
        try {
            const { data } = await logoutUser()
            console.log("logoutUser data: ", data)
            // setLogoutLoading(false)
            if (data?.logoutUser) {
                safeStorage.removeItem("accessToken")
                safeStorage.removeItem("refreshToken")
                safeStorage.removeItem("booker")
                safeStorage.removeItem("attendeeId")
                safeStorage.removeItem("loggedInUser")
                safeStorage.removeItem("bookingData")
                safeStorage.removeItem("course")
            }
            setLogoutLoading(false)
        } catch (error) {
            setLogoutLoading(false)
            console.log("err====>>>>", error)
        }
        history.push(goToLink)
    }

    const resultProps: { [key in ResultKeys]: { resultProps: ResultProps } } = {
        succesfullyBooked: {
            resultProps: {
                status: "success",
                title: t("resultScreen.succesfullyBooked.title", "Die Buchung wurde erfolgreich durchgeführt."),
                subTitle: t("resultScreen.succesfullyBooked.subTitle", "Du solltest in Kürze eine Bestätigungsmail erhalten."),
                extra: [appliedDiscountLine, <StandardButton key='standard' />].filter(Boolean),
            },
        },
        successfullyOnWaitingList: {
            resultProps: {
                status: "success",
                title: <p>{t("resultScreen.successfullyOnWaitingList.title", "Wir haben dich oder dein Kind auf die Warteliste gesetzt.")}</p>,
                subTitle: (
                    <p>
                        {waitlistBecausePrerequisiteMissing
                            ? t(
                                  "resultScreen.successfullyOnWaitingList.subTitlePrerequisite",
                                  "Da der Voraussetzungskurs fehlt, haben wir dich bzw. dein Kind nur auf die Warteliste gesetzt. Ein Platz ist damit noch nicht gebucht."
                              )
                            : waitlistReason === "waitlistOnly"
                            ? t("resultScreen.successfullyOnWaitingList.subTitleWaitlistOnly", "Ein Platz ist damit noch nicht gebucht.")
                            : t("resultScreen.successfullyOnWaitingList.subTitlePartOne", "Da dieser Kurs bereits ausgebucht ist, haben wir dich bzw. dein Kind, auf die Warteliste gesetzt.")}
                        <br />
                        {t("resultScreen.successfullyOnWaitingList.subTitlePartTwo", "Du solltest in Kürze eine Bestätigungsmail erhalten.")}
                    </p>
                ),
                extra: [appliedDiscountLine, <StandardButton key='standard' />].filter(Boolean),
            },
        },
        interestRegistered: {
            resultProps: {
                status: "success",
                title: t("interest.success.title", "Dein Interesse wurde registriert"),
                subTitle: t("interest.success.mailHint", "Du erhältst in Kürze eine Bestätigung per E-Mail."),
                extra: [<StandardButton />],
            },
        },
        paymentInitFailed: {
            resultProps: {
                status: "warning",
                title: t("resultScreen.paymentInitFailed.title", "Zahlung konnte nicht gestartet werden"),
                subTitle: t(
                    "resultScreen.paymentInitFailed.subTitle",
                    "Deine Buchung wurde registriert, aber die Zahlung konnte nicht gestartet werden. Bitte kontaktiere den Kursanbieter."
                ),
                extra: [<StandardButton />],
            },
        },
        loggedOut: {
            resultProps: {
                status: "warning",
                title: t("resultScreen.loggedOut.title", "Du wurdest ausgeloggt."),
                subTitle: t("resultScreen.loggedOut.subTitle", "Bitte logge dich erneut ein."),
                extra: [
                    <Button key='login' type='primary' onClick={() => logout({ goToLink: URL_LOGIN })} loading={logoutLoading}>
                        {t("resultScreen.loggedOut.extra.login", "Erneut anmelden")}
                    </Button>,
                    <Button key='backToSearch' type='ghost' onClick={() => logout({ goToLink: URL_BASE })}>
                        {t("resultScreen.loggedOut.extra.backToSearch", "Zurück zur Kurssuche")}
                    </Button>,
                ],
            },
        },
        success: {
            resultProps: {
                status: "success",
                title: t("resultScreen.success.title", "Erfolgreich"),
                subTitle: t("resultScreen.success.subTitle", "Die Buchung wurde erfolgreich durchgeführt."),
                extra: [<StandardButton />],
            },
        },
        test: {
            resultProps: {
                status: "success",
                title: t("resultScreen.test.title", "Test"),
                subTitle: t("resultScreen.test.subTitle", "Das ist ein Test."),
                extra: [<StandardButton />],
            },
        },
        alreadyEnrolled: {
            resultProps: {
                status: "warning",
                title: t("resultScreen.alreadyEnrolled.title", "Doppelbuchung"),
                subTitle: t("resultScreen.alreadyEnrolled.subTitle", "Teilnehmer:in ist bereits in diesen Kurs gebucht."),
                extra: [<StandardButton />],
            },
        },
        courseNotFound: {
            resultProps: {
                status: "warning",
                title: t("resultScreen.courseNotFound.title", "Kurs nicht gefunden"),
                subTitle: t("resultScreen.courseNotFound.subTitle", "Leider haben wir den Kurs nicht gefunden. Eventuell ist dieser nicht mehr aktuell."),
                extra: [<StandardButton />],
            },
        },
        notDeletableHasBookings: {
            resultProps: {
                status: "warning",
                title: t("resultScreen.notDeletableHasBookings.title", "Teilnehmer kann nicht gelöscht werden"),
                subTitle: t("resultScreen.notDeletableHasBookings.subTitle", "Da dieser Teilnehmer bereits in einen Kurs gebucht ist, kann er nicht gelöscht werden."),
                extra: [<BackButton />],
            },
        },
        errorFetchingData: {
            resultProps: {
                status: "warning",
                title: t("resultScreen.errorFetchingData.title", "Fehler beim Laden der Daten"),
                subTitle: t("resultScreen.errorFetchingData.subTitle", "Es gab eine Fehler beim Abrufen der Daten. Bitte versuche es in ein paar Minuten erneut"),
                extra: [
                    <StandardButton />,
                    <Button icon={<ReloadOutlined />} onClick={() => window.location.reload()}>
                        {t("resultScreen.errorFetchingData.extra.reload", "Seite neu laden")}
                    </Button>,
                ],
            },
        },
        bookingRestrictionError: {
            resultProps: {
                status: "warning",
                title: t("resultScreen.bookingRestrictionError.title", "Buchungseinschränkung"),
                subTitle: t("resultScreen.bookingRestrictionError.subTitle", "Die Buchungseinschränkung ist nicht erfüllt."),
                // extra: [<StandardButton />],
                extra: [<BackButton />],
            },
        },
        NoBookingRestrictionFound: {
            resultProps: {
                status: "warning",
                title: t("resultScreen.NoBookingRestrictionFound.title", "Keine Buchungseinschränkung gefunden"),
                subTitle: t("resultScreen.NoBookingRestrictionFound.subTitle", bookingRestrictionErrorTranslations[BookingRestrictionErrors.NoBookingRestrictionFound]),
                // extra: [<StandardButton />],
                extra: [<BackButton />],
            },
        },
        BookingRestrictionNotWithinTimeFrame: {
            resultProps: {
                status: "warning",
                title: t("resultScreen.BookingRestrictionNotWithinTimeFrame.title", "Buchungseinschränkung"),
                subTitle: t("resultScreen.BookingRestrictionNotWithinTimeFrame.subTitle", bookingRestrictionErrorTranslations[BookingRestrictionErrors.BookingRestrictionNotWithinTimeFrame]),
                // extra: [<StandardButton />],
                extra: [<BackButton />],
            },
        },
        BookingRestrictionExpiredAndShowToAllIsSet: {
            resultProps: {
                status: "warning",
                title: t("resultScreen.BookingRestrictionExpiredAndShowToAllIsSet.title", "Buchungseinschränkung"),
                subTitle: t(
                    "resultScreen.BookingRestrictionExpiredAndShowToAllIsSet.subTitle",
                    bookingRestrictionErrorTranslations[BookingRestrictionErrors.BookingRestrictionExpiredAndShowToAllIsSet]
                ),
                // extra: [<StandardButton />],
                extra: [<BackButton />],
            },
        },
        AttendeeHasNoBookingInPrerequisiteCourse: {
            resultProps: {
                status: "warning",
                title: t("resultScreen.AttendeeHasNoBookingInPrerequisiteCourse.title", "Buchungseinschränkung"),
                subTitle: t("resultScreen.AttendeeHasNoBookingInPrerequisiteCourse.subTitle", bookingRestrictionErrorTranslations[BookingRestrictionErrors.AttendeeHasNoBookingInPrerequisiteCourse]),
                // extra: [<StandardButton />],
                extra: [<BackButton />],
            },
        },
        AttendeeHasWaitlistBookingInPrerequisiteCourse: {
            resultProps: {
                status: "warning",
                title: t("resultScreen.AttendeeHasWaitlistBookingInPrerequisiteCourse.title", "Buchungseinschränkung"),
                subTitle: t(
                    "resultScreen.AttendeeHasWaitlistBookingInPrerequisiteCourse.subTitle",
                    bookingRestrictionErrorTranslations[BookingRestrictionErrors.AttendeeHasWaitlistBookingInPrerequisiteCourse]
                ),
                // extra: [<StandardButton />],
                extra: [<BackButton />],
            },
        },
        AttendeeHasBookingInPrerequisiteCourse: {
            resultProps: {
                status: "warning",
                title: t("resultScreen.AttendeeHasBookingInPrerequisiteCourse.title", "Buchungseinschränkung"),
                subTitle: t("resultScreen.AttendeeHasBookingInPrerequisiteCourse.subTitle", bookingRestrictionErrorTranslations[BookingRestrictionErrors.AttendeeHasBookingInPrerequisiteCourse]),
                // extra: [<StandardButton />],
                extra: [<BackButton />],
            },
        },
        AttendeeIsNotAMember: {
            resultProps: {
                status: "warning",
                title: t("resultScreen.AttendeeIsNotAMember.title", "Buchungseinschränkung"),
                subTitle: t("resultScreen.AttendeeIsNotAMember.subTitle", bookingRestrictionErrorTranslations[BookingRestrictionErrors.AttendeeIsNotAMember]),
                // extra: [<StandardButton />],
                extra: [<BackButton />],
            },
        },
        AllowedToBook: {
            resultProps: {
                status: "warning",
                title: t("resultScreen.AllowedToBook.title", "Buchungseinschränkung"),
                subTitle: t("resultScreen.AllowedToBook.subTitle", bookingRestrictionErrorTranslations[BookingRestrictionErrors.AllowedToBook]),
                // extra: [<StandardButton />],
                extra: [<BackButton />],
            },
        },
        lateEntryNoEligibleLessons: {
            resultProps: {
                status: "warning",
                title: t("resultScreen.lateEntryNoEligibleLessons.title", "Buchung nicht mehr möglich"),
                subTitle: t(
                    "resultScreen.lateEntryNoEligibleLessons.subTitle",
                    "Dieser Kurs ist bereits abgeschlossen und kann nicht mehr gebucht werden.",
                ),
                extra: [<StandardButton />],
            },
        },
        lateEntryMinimumNotReached: {
            resultProps: {
                status: "warning",
                title: t("resultScreen.lateEntryMinimumNotReached.title", "Buchung nicht mehr möglich"),
                subTitle: t(
                    "resultScreen.lateEntryMinimumNotReached.subTitle",
                    "Für diesen Kurs sind nicht mehr genügend Kurseinheiten verfügbar, eine Buchung ist nicht mehr möglich.",
                ),
                extra: [<StandardButton />],
            },
        },
        lateEntryPricingUnavailable: {
            resultProps: {
                status: "warning",
                title: t("resultScreen.lateEntryPricingUnavailable.title", "Buchung derzeit nicht möglich"),
                subTitle: t(
                    "resultScreen.lateEntryPricingUnavailable.subTitle",
                    "Für diesen Kurs kann der Preis aktuell nicht ermittelt werden. Bitte wende dich an den Kursanbieter.",
                ),
                extra: [<StandardButton />],
            },
        },
        unknown: {
            resultProps: {
                status: "warning",
                title: standardErrorTitle,
                subTitle: errorMsg,
                extra: [<StandardButton />],
            },
        },
    }

    let props = resultProps.unknown.resultProps

    if (!resultType) return <div>Error</div>
    if (!isOfTypeResultKeys(resultType as string)) {
        props = resultProps.unknown.resultProps
    } else {
        props = resultProps[resultType as ResultKeys]?.resultProps
    }

    return (
        <Result status={props.status} title={props.title} subTitle={props.subTitle} extra={props.extra} style={props.style}>
            {props.children}
            {customButton}
        </Result>
    )
}

export default ResultScreen

export const bookingRestrictionErrorTranslations = {
    [BookingRestrictionErrors.NoBookingRestrictionFound]: "Keine Buchungseinschränkung gefunden",
    [BookingRestrictionErrors.BookingRestrictionNotWithinTimeFrame]: "Buchungsbeschränkung ist nicht innerhalb des Zeitrahmens",
    [BookingRestrictionErrors.BookingRestrictionExpiredAndShowToAllIsSet]: "Buchungsbeschränkung ist ausgelaufen und nicht mehr gültig",
    [BookingRestrictionErrors.AttendeeHasNoBookingInPrerequisiteCourse]: "Teilnehmer hat keine Buchung in einem Voraussetzungskurs",
    [BookingRestrictionErrors.AttendeeHasWaitlistBookingInPrerequisiteCourse]: "Teilnehmer hat eine Warteliste in einem Voraussetzungskurs",
    [BookingRestrictionErrors.AttendeeHasBookingInPrerequisiteCourse]: "Teilnehmer hat eine Buchung in einem Voraussetzungskurs",
    [BookingRestrictionErrors.AttendeeIsNotAMember]: "Teilnehmer ist kein Mitglied",
    [BookingRestrictionErrors.AllowedToBook]: "Erlaubt zu buchen",
}
